import { z } from 'zod';
import { foldText } from './normalize.ts';

/** Request schemas and response types of /api/challenges ("Reto cofrade"). */

export const CHALLENGE_LIMITS = {
  questions: { min: 5, max: 20, default: 10 },
  seconds: { min: 10, max: 60, default: 20 },
  nickname: { min: 2, max: 20 },
} as const;

/**
 * Basic filter of offensive words in Spanish, checked on the folded nickname with
 * common letter swaps undone ("put0" → "puto"). It is a game among friends: this only
 * keeps the obvious out of a shared leaderboard.
 */
/** Offensive as a whole word ("marica" must not catch "Maricarmen"). */
const OFFENSIVE_WORDS = new Set(
  (
    'puta puto putas putos polla pollas cono joder zorra marica hdp nazi facha pene ' +
    'chocho culo hostia mamon mamona cabron cabrona'
  ).split(' '),
);
/** Offensive anywhere, even glued to other words ("ElHijoputa"). */
const OFFENSIVE_FRAGMENTS = (
  'mierda cabron gilipollas hijoputa hijodeputa subnormal retrasado mongolo imbecil ' +
  'capullo cojones maricon negrata sudaca pajillero follar'
).split(' ');
const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't' };

/** Key under which two nicknames are the same: no case, accents or extra spaces. */
export const nicknameKey = (nickname: string): string => foldText(nickname);

export type NicknameProblem = 'too_short' | 'too_long' | 'invalid_characters' | 'offensive';

export function nicknameProblem(raw: string): NicknameProblem | null {
  const nickname = raw.trim().replace(/\s+/g, ' ');
  if (nickname.length < CHALLENGE_LIMITS.nickname.min) return 'too_short';
  if (nickname.length > CHALLENGE_LIMITS.nickname.max) return 'too_long';
  if (!/^[\p{L}\p{N} ._'+-]+$/u.test(nickname) || nicknameKey(nickname).length < 2) {
    return 'invalid_characters';
  }
  const folded = foldText(nickname.replace(/[@$]/g, (c) => (c === '@' ? 'a' : 's')));
  const unleet = folded.replace(/[0-9]/g, (d) => LEET[d] ?? d);
  const words = [...folded.split(' '), ...unleet.split(' ')];
  const joined = [folded.replace(/ /g, ''), unleet.replace(/ /g, '')];
  if (
    words.some((w) => OFFENSIVE_WORDS.has(w)) ||
    OFFENSIVE_FRAGMENTS.some((f) => joined.some((j) => j.includes(f)))
  ) {
    return 'offensive';
  }
  return null;
}

export const NICKNAME_MESSAGES: Record<NicknameProblem | 'taken', string> = {
  too_short: 'El apodo debe tener al menos 2 caracteres.',
  too_long: 'El apodo puede tener como mucho 20 caracteres.',
  invalid_characters: 'Usa letras, números, espacios o . - _',
  offensive: 'Elige otro apodo, por favor.',
  taken: 'Ese apodo ya está cogido en este reto.',
};

const nickname = z
  .string()
  .transform((s) => s.trim().replace(/\s+/g, ' '))
  .superRefine((s, ctx) => {
    const problem = nicknameProblem(s);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

export const createChallengeSchema = z.object({
  nickname,
  question_count: z
    .number()
    .int()
    .min(CHALLENGE_LIMITS.questions.min)
    .max(CHALLENGE_LIMITS.questions.max)
    .default(CHALLENGE_LIMITS.questions.default),
  seconds_per_question: z
    .number()
    .int()
    .min(CHALLENGE_LIMITS.seconds.min)
    .max(CHALLENGE_LIMITS.seconds.max)
    .default(CHALLENGE_LIMITS.seconds.default),
});
export type CreateChallengeRequest = z.input<typeof createChallengeSchema>;

export const joinChallengeSchema = z.object({ nickname });
export type JoinChallengeRequest = z.input<typeof joinChallengeSchema>;

export const answerSchema = z.object({
  position: z
    .number()
    .int()
    .min(0)
    .max(CHALLENGE_LIMITS.questions.max - 1),
  /** Index of the option as shown to this player; null = "I let the time run out" */
  choice: z.number().int().min(0).max(3).nullable(),
  /** Same key on every retry of the same submission */
  idempotency_key: z.string().min(8).max(64),
});
export type AnswerRequest = z.infer<typeof answerSchema>;

/** Code in the link: 8 characters without look-alikes (no 0/o, 1/l/i). */
export const CHALLENGE_CODE = /^[a-z2-9]{8}$/;

export interface LeaderboardEntry {
  rank: number;
  nickname: string;
  correct: number;
  total_ms: number;
  finished_at: string;
  is_creator: boolean;
}

export interface ChallengeInfo {
  code: string;
  creator: string;
  question_count: number;
  seconds_per_question: number;
  data_year: number;
  created_at: string;
  expires_at: string;
  status: 'open' | 'closed';
  /** Finished players, ranked */
  leaderboard: LeaderboardEntry[];
  /** Players still playing (nicknames only) */
  playing: string[];
  server_now: string;
  /** Only with a valid device token: how this player is doing */
  me?: MyStatus | null;
}

export interface MyStatus {
  nickname: string;
  is_creator: boolean;
  state: 'not_started' | 'playing' | 'finished';
  answered: number;
}

export interface JoinResult {
  token: string;
  nickname: string;
}

export interface CreateChallengeResult extends JoinResult {
  code: string;
  /** Path of the challenge page: /reto/{code} */
  path: string;
}

/** A question as served: never with the answer. */
export interface ServedQuestion {
  status: 'question';
  position: number;
  total: number;
  prompt: string;
  negative: boolean;
  options: string[];
  served_at: string;
  /** Answers after deadline + grace count as wrong */
  deadline: string;
  seconds: number;
  /** For the countdown: the server's clock when it answered */
  server_now: string;
  extra?: Record<string, unknown>;
}

export interface GameFinished {
  status: 'finished';
}

export type NextResult = ServedQuestion | GameFinished;

export interface AnswerResult {
  position: number;
  correct: boolean;
  /** Index of the right option, as this player sees them */
  correct_choice: number;
  /** The player's choice as recorded (null if the time ran out) */
  choice: number | null;
  /** Arrived after the deadline (+ grace): counted as wrong */
  late: boolean;
  /** This answer was recorded before (a retry): the original result is returned */
  repeated: boolean;
  finished: boolean;
}

export interface ResultQuestion {
  position: number;
  prompt: string;
  negative: boolean;
  /** In the order this player saw them */
  options: string[];
  correct_choice: number;
  choice: number | null;
  correct: boolean;
  response_ms: number | null;
  explanation: string;
  source: string;
  data_year: number;
  extra?: Record<string, unknown>;
  /** Other finished players' answers to this question */
  others: { nickname: string; answer: string | null; correct: boolean }[];
}

export interface ChallengeResults {
  status: 'ok';
  me: { nickname: string; correct: number; total_ms: number; rank: number | null };
  questions: ResultQuestion[];
  leaderboard: LeaderboardEntry[];
  playing: string[];
}

export interface ResultsNotFinished {
  status: 'not_finished';
  leaderboard: LeaderboardEntry[];
  playing: string[];
}

export type ChallengeErrorCode =
  | 'invalid_request'
  | 'not_found'
  | 'closed'
  | 'nickname_taken'
  | 'nickname_invalid'
  | 'unauthorized'
  | 'already_joined'
  | 'not_served'
  | 'not_enough_questions'
  | 'rate_limited';

export interface ChallengeError {
  error: ChallengeErrorCode;
  /** For nickname_invalid: which rule */
  reason?: NicknameProblem;
  message?: string;
}

/** [lat, lon], as Leaflet takes them. */
export type LatLon = [number, number];

/**
 * What a map question draws (`extra.map`). Nothing in it names a street, a church or a
 * brotherhood: labels are only numbers.
 */
export type MapSpec =
  /** One route: "whose is it?" */
  | { kind: 'route'; lines: LatLon[][]; start: LatLon }
  /** Several routes, numbered, in the same frame: "which one is X's?" */
  | { kind: 'routes'; routes: { label: string; lines: LatLon[][] }[] }
  /** Numbered points: "which one does X leave from?" */
  | { kind: 'points'; points: { label: string; at: LatLon }[] };
